import { Eye, EyeOff, Loader2 } from "lucide-react"
import * as React from "react"
import { useNavigate, useSearchParams } from "react-router"

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"

import { AuthLayout } from "./auth-layout"
import { useAcceptInviteMutation, useCurrentUser, type AcceptInviteError } from "./auth-store"

const MIN_PASSWORD_LENGTH = 8

interface FieldErrors {
  password: string | null
  confirm: string | null
}

function validate(password: string, confirm: string): FieldErrors {
  return {
    password:
      password.length === 0
        ? "Choose a password."
        : password.length < MIN_PASSWORD_LENGTH
          ? `At least ${MIN_PASSWORD_LENGTH} characters.`
          : null,
    confirm: confirm !== password ? "Passwords do not match." : null,
  }
}

function hasError(errors: FieldErrors): boolean {
  return errors.password !== null || errors.confirm !== null
}

export function AcceptInvitePage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")

  const accept = useAcceptInviteMutation()
  const currentUser = useCurrentUser()

  // A signed-in user who opens someone else's invite must NOT be silently redirected past the
  // acceptance form — the invitation would go unspent. Only bounce if there is no token to
  // accept (the page was opened without one).
  React.useEffect(() => {
    if (currentUser && !token) navigate("/reviews", { replace: true })
  }, [currentUser, token, navigate])

  const [fullName, setFullName] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [confirm, setConfirm] = React.useState("")
  const [showPassword, setShowPassword] = React.useState(false)
  const [attempted, setAttempted] = React.useState(false)

  const fieldErrors = React.useMemo<FieldErrors>(() => {
    if (!attempted) return { password: null, confirm: null }
    return validate(password, confirm)
  }, [attempted, password, confirm])

  if (!token) {
    return (
      <AuthLayout
        title="Invitation link missing"
        subtitle="This page expects an invitation token in the URL."
      >
        <Alert tone="error" size="sm" title="No invitation token">
          Ask your Pyxis admin to resend the invitation.
        </Alert>
      </AuthLayout>
    )
  }

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setAttempted(true)
    const errors = validate(password, confirm)
    if (hasError(errors) || accept.isPending) return
    try {
      await accept.mutateAsync({ token, newPassword: password, fullName: fullName || null })
      navigate("/reviews", { replace: true })
    } catch {
      /* surfaced via accept.error */
    }
  }

  const topError = toTopError(accept.error)

  return (
    <AuthLayout
      title="Set your password"
      subtitle="You've been invited to PyxisRC Label Review. Choose a password to activate your account."
    >
      <form
        onSubmit={onSubmit}
        className="space-y-4"
        noValidate
        aria-busy={accept.isPending || undefined}
      >
        <div className="sr-only" aria-live="polite" role="status">
          {accept.isPending ? "Setting your password…" : topError ? topError.body : ""}
        </div>

        {topError && (
          <Alert tone="error" size="sm" title={topError.title}>
            {topError.body}
          </Alert>
        )}

        {currentUser && (
          <Alert tone="warning" size="sm" title={`Signed in as ${currentUser.email}`}>
            Setting a password here will replace your current session with the invitee's account.
          </Alert>
        )}

        <FormField label="Full name" hint="Optional — shown to teammates on comments and reviews.">
          <Input
            type="text"
            autoComplete="name"
            inputSize="lg"
            value={fullName}
            onChange={(e) => setFullName(e.currentTarget.value)}
            disabled={accept.isPending}
            autoFocus
          />
        </FormField>

        <FormField label="New password" required error={fieldErrors.password}>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              inputSize="lg"
              value={password}
              onChange={(e) => {
                setPassword(e.currentTarget.value)
                if (accept.error) accept.reset()
              }}
              disabled={accept.isPending}
              wrapperClassName="pr-9"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              tabIndex={-1}
              disabled={accept.isPending}
              className="absolute right-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-sm text-text-3 transition-colors duration-120 ease-out hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)] disabled:opacity-50"
            >
              {showPassword ? (
                <EyeOff className="size-4" aria-hidden="true" />
              ) : (
                <Eye className="size-4" aria-hidden="true" />
              )}
            </button>
          </div>
        </FormField>

        <FormField label="Confirm password" required error={fieldErrors.confirm}>
          <Input
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            inputSize="lg"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.currentTarget.value)
              if (accept.error) accept.reset()
            }}
            disabled={accept.isPending}
          />
        </FormField>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          leadingIcon={accept.isPending ? <Loader2 className="animate-spin" /> : undefined}
          disabled={accept.isPending}
        >
          {accept.isPending ? "Setting up…" : "Set password and sign in"}
        </Button>
      </form>
    </AuthLayout>
  )
}

interface TopError {
  title: string
  body: string
}

function toTopError(error: AcceptInviteError | null): TopError | null {
  if (!error) return null
  switch (error.kind) {
    case "invalid_or_expired":
      return {
        title: "Invitation invalid or expired",
        body: "Ask your Pyxis admin to resend the invitation.",
      }
    case "network":
      return {
        title: "Network problem",
        body: "We couldn't reach the server. Check your connection and try again.",
      }
    case "server":
      return {
        title: "Server error",
        body: "Something went wrong on our side. Please try again in a moment.",
      }
    default:
      return { title: "Could not accept invitation", body: error.message }
  }
}
